/**
 * ¿QUÉ CUENTA COMO MENSAJE DEL CLIENTE? — la regla, en un solo sitio.
 *
 * ⚠️ COPIA BYTE-IDÉNTICA en `src/utils/mensajeDeCliente.js`. `src/` no puede importar
 * de `functions/` y las dos mitades del producto necesitan la misma respuesta: el
 * backend para decidir si el bot contesta y si la ventana está abierta, la UI para
 * pintar la burbuja y para avisar antes de escribir. Lo sostiene
 * `functions/scripts/check-shared-module-parity.js`.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POR QUÉ NACE ESTE MÓDULO: HABÍA TRES COPIAS DE MEDIA REGLA
 * ───────────────────────────────────────────────────────────────────────────────
 * «Es del cliente» estaba escrito tres veces, y ya habían divergido en forma:
 *
 *   · `generateAlerts.js:1033`   — `esDeClienteEnChat`, función con nombre
 *   · `sendMessageCore.js:572`   — inline, dentro del barrido de 50 mensajes
 *   · `ChatDetalle.js:797`       — inline, dentro de un `filter`
 *
 * Añadir «y además no es un comentario» en tres sitios es garantizar que el cuarto
 * llamante se olvide. Por eso se unifica la regla ENTERA y no sólo la mitad nueva:
 * dejar tres copias de `esDeCliente` y una función compartida para `esComentario`
 * habría sido peor que el estado de partida.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL CASO QUE LO PRODUJO (1 y 3 de septiembre, y otra vez el 21)
 * ───────────────────────────────────────────────────────────────────────────────
 * `igWebhook.js:307` escribe los comentarios de Instagram en el chat de DM de esa
 * persona —`threadId = from.id`, a propósito— para que el bot «tome el hilo»
 * (COMMENT-RULES-SCHEMA.md §3.4). Entran como `author.type: 'customer'`.
 *
 * El bot los trataba como un turno de conversación y respondía por DM. Meta lo
 * rechazaba con `WINDOW_EXPIRED` **y tenía razón**: la persona nunca había abierto
 * conversación privada. Nuestro historial decía «escribió hace 0,0 h» porque el
 * comentario estaba en el chat de DM.
 *
 * Medido: 12 comentarios inyectados en 6 chats, y 4 respuestas del bot rechazadas.
 * Durante semanas eso se leyó como «Instagram rechaza con la ventana abierta».
 *
 * ⚠️ EL PRIVATE REPLY NO ABRE LA VENTANA. Es un permiso de un solo disparo atado al
 * `comment_id`, no una conversación. Por eso el handoff de §3.4 no puede ocurrir al
 * comentar: ocurre cuando **la persona responde**, y esa respuesta sí es un DM real.
 * `handoffToBot` conserva su sentido; lo que cambia es CUÁNDO se cumple.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * DOS PREGUNTAS, DOS FUNCIONES
 * ───────────────────────────────────────────────────────────────────────────────
 * El RENDER necesita saber que algo es un comentario para pintarlo como tal. La
 * VENTANA necesita saber que no abre nada. Fundirlas obligaría al render a preguntar
 * por la ventana, que no es asunto suyo.
 */

/**
 * ¿Lo escribió el cliente (y no el negocio)?
 *
 * Las tres formas conviven por razones históricas y las tres se aceptan: 928 mensajes
 * llevan `direccion` y `direction`, 799 sólo `direction`, 36 sólo `direccion`.
 * `remitente` es el tercer respaldo. Ver la nota de la forma del mensaje en CLAUDE.md.
 */
function esDeCliente(msg) {
  if (!msg || typeof msg !== 'object') return false;
  if (msg.direction === 'in') return true;
  if (msg.direccion === 'entrante') return true;
  return String(msg.remitente || '').toLowerCase() === 'cliente';
}

/**
 * ¿Es un comentario de Instagram inyectado en el chat de DM?
 *
 * ⚠️ EXIGE LA MARCA, NO LA INFIERE. Un falso positivo deja al bot mudo ante un DM
 * real, que es un daño peor que el que esto evita: el comentario ya recibió respuesta
 * pública y privada de la automatización, mientras que un DM sin contestar es un
 * cliente ignorado. Por eso no se mira el canal, ni el texto, ni la ausencia de
 * adjuntos — sólo las dos marcas que `writeCommentToInbox` escribe siempre.
 *
 * El respaldo por `externalId` no es redundante: los 12 comentarios históricos lo
 * llevan, y `meta` es un objeto que algún camino podría no propagar. El prefijo
 * `comment_` lo pone el mismo sitio (`igWebhook.js:311`) y no colisiona con un `mid`
 * de DM, que es lo que documenta su comentario.
 */
function esComentario(msg) {
  if (!msg || typeof msg !== 'object') return false;
  if (msg.meta && msg.meta.source === 'ig_comment') return true;
  return typeof msg.externalId === 'string' && msg.externalId.startsWith('comment_');
}

/**
 * ¿Es la respuesta privada que la automatización mandó a un comentario?
 *
 * Se persiste con `remitente: 'bot'` —lo es— así que sin esta marca la burbuja sería
 * indistinguible de una respuesta del bot en una conversación. El operador tiene que
 * poder separar lo que dijo la automatización de lo que dijo el bot.
 */
function esRespuestaPrivada(msg) {
  return Boolean(msg && msg.meta && msg.meta.source === 'ig_private_reply');
}

/**
 * ¿Lo importó el historial de coexistence (C6, 27-oct-2026)? `integrations/coexistence/historial.js` escribe
 * `meta.origen: 'historial'`. Es pasado: la ventana de 24 h sólo la abren los mensajes llegados por la API.
 */
function esDeHistorial(msg) {
  return Boolean(msg && msg.meta && msg.meta.origen === 'historial');
}

/**
 * ¿Este mensaje ABRE la ventana de atención de 24 h de Meta?
 *
 * Es la pregunta que hay que hacerle al historial antes de decidir si se puede
 * escribir texto libre. Un comentario NO la abre — lo dijo Meta rechazando cuatro
 * envíos— y confundirlo con un DM es exactamente el fallo de septiembre. Y un mensaje
 * importado del historial de la app Business (C6) tampoco: es pasado.
 *
 * El nombre no menciona Instagram a propósito: la regla es de VENTANA. El día que
 * otro canal inyecte algo parecido, el sitio a tocar es éste y no tres.
 */
function abreVentana(msg) {
  return esDeCliente(msg) && !esComentario(msg) && !esDeHistorial(msg);
}

module.exports = { esDeCliente, esComentario, esRespuestaPrivada, esDeHistorial, abreVentana };
